export const lifecycleStates = [
  'DRAFT',
  'NEEDS_CLARIFICATION',
  'READY',
  'IN_PROGRESS',
  'BLOCKED',
  'READY_FOR_VERIFICATION',
  'VERIFICATION_FAILED',
  'AWAITING_APPROVAL',
  'VERIFIED_DONE',
  'CANCELLED',
] as const

export type LifecycleState = (typeof lifecycleStates)[number]
export type VerificationResult = 'PASS' | 'FAIL' | 'NOT CHECKABLE' | 'N/A'
export type Severity = 'Blocker' | 'Critical' | 'Major' | 'Minor'

export type DoneGateInput = {
  hasWorkSpec: boolean
  hasPlan: boolean
  hasCompletedExecution: boolean
  evidenceCount: number
  verificationResults: VerificationResult[]
  defects: Array<{ severity: Severity; state: 'OPEN' | 'RESOLVED' | 'WAIVED' }>
  factualIntegrityConfirmed: boolean
  acceptanceVerified: boolean
  approvalGranted: boolean
}

export type DoneGateResult = {
  pass: boolean
  missing: string[]
}

const transitions: Record<LifecycleState, LifecycleState[]> = {
  DRAFT: ['NEEDS_CLARIFICATION', 'READY', 'CANCELLED'],
  NEEDS_CLARIFICATION: ['READY', 'CANCELLED'],
  READY: ['IN_PROGRESS', 'BLOCKED', 'CANCELLED'],
  IN_PROGRESS: ['BLOCKED', 'READY_FOR_VERIFICATION', 'CANCELLED'],
  BLOCKED: ['IN_PROGRESS', 'CANCELLED'],
  READY_FOR_VERIFICATION: ['VERIFICATION_FAILED', 'AWAITING_APPROVAL', 'BLOCKED', 'CANCELLED'],
  VERIFICATION_FAILED: ['IN_PROGRESS', 'BLOCKED', 'CANCELLED'],
  AWAITING_APPROVAL: ['VERIFIED_DONE', 'VERIFICATION_FAILED', 'CANCELLED'],
  VERIFIED_DONE: [],
  CANCELLED: [],
}

export function canTransition(from: string, to: string): boolean {
  if (!lifecycleStates.includes(from as LifecycleState)) return false
  if (!lifecycleStates.includes(to as LifecycleState)) return false
  return transitions[from as LifecycleState].includes(to as LifecycleState)
}

export function evaluateDoneGate(input: DoneGateInput): DoneGateResult {
  const missing: string[] = []
  if (!input.hasWorkSpec) missing.push('Thiếu WorkSpec hiện hành.')
  if (!input.hasPlan) missing.push('Thiếu Route/Plan hiện hành.')
  if (!input.hasCompletedExecution) missing.push('Chưa có execution COMPLETED.')
  if (input.evidenceCount < 1) missing.push('Thiếu evidence.')
  if (input.verificationResults.length < 1) missing.push('Thiếu verification độc lập.')
  if (input.verificationResults.some((result) => result === 'FAIL' || result === 'NOT CHECKABLE')) {
    missing.push('Còn tiêu chí FAIL hoặc NOT CHECKABLE.')
  }
  if (!input.verificationResults.includes('PASS')) missing.push('Chưa có tiêu chí PASS.')
  if (input.defects.some((defect) => (defect.severity === 'Blocker' || defect.severity === 'Critical') && defect.state !== 'RESOLVED')) {
    missing.push('Còn Blocker/Critical chưa xử lý; các mức này không được waiver.')
  }
  if (input.defects.some((defect) => defect.state === 'OPEN' && defect.severity === 'Major')) {
    missing.push('Còn Major chưa resolved/waived.')
  }
  if (!input.factualIntegrityConfirmed) missing.push('Chưa xác nhận factual integrity D-032.')
  if (!input.acceptanceVerified) missing.push('Chưa có verification PASS cho tiêu chí nghiệm thu/DoD.')
  if (!input.approvalGranted) missing.push('Thiếu approval rõ ràng.')
  return { pass: missing.length === 0, missing }
}

export function buildPortableExport(input: Record<string, unknown>) {
  return {
    export_schema: 'idone.mvp.handover.v1',
    exported_at: new Date().toISOString(),
    ...input,
  }
}
